import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useTranslation } from "react-i18next";
import axios from "axios";

import { AddHeader } from "../../components/AddHeader";
import { ExerciseCard } from "../../components/ExerciseCard";
import {
  addexercise,
  getexercise,
  exdetails,
} from "../../components/funtionsBD/Mainservice";
import Loader from "../../components/Loading/Loader";
import { ExerciseAddLayout } from "./ExerciseAddLayout";
import { ip_back } from "../../config";
import { localizedExerciseName, localizedField } from "../../utils/localizedCatalogFields";

const DEFAULT_THEME_EN = "Technical";

function themeIdOf(theme) {
  return theme?.th_id || theme?.theme_id || theme?.the_id || null;
}

function themeNameOf(theme, lng) {
  if (!theme) return "";
  return localizedField(theme, "theme_name", lng) || theme.name || "";
}

/** Difficulty level of `themeId` on an exercise; null if the theme is not linked. */
function getExerciseThemeLevel(exercise, themeId) {
  if (!themeId || !exercise) return null;
  const themes = exercise.themes || [];
  const match = themes.find(
    (t) => String(themeIdOf(t)) === String(themeId)
  );
  if (!match) return null;
  const level = Number(match.level ?? match.the_lavel);
  return Number.isFinite(level) ? level : null;
}

function exerciseDisplayName(exercise, lng) {
  return localizedExerciseName(exercise, lng);
}

function sortExercisesByThemeLevel(exercises, themeId, lng) {
  return [...(exercises || [])].sort((a, b) => {
    const levelA = getExerciseThemeLevel(a, themeId);
    const levelB = getExerciseThemeLevel(b, themeId);
    const hasA = levelA != null;
    const hasB = levelB != null;
    if (hasA && hasB && levelA !== levelB) return levelA - levelB;
    if (hasA && !hasB) return -1;
    if (!hasA && hasB) return 1;
    return exerciseDisplayName(a, lng).localeCompare(
      exerciseDisplayName(b, lng),
      undefined,
      { sensitivity: "base" }
    );
  });
}

export const EOut2 = () => {
  const [list, setlist] = useState([]);
  const [error, seterror] = useState();
  const [open, setopen] = useState(false);
  const [loading, setloading] = useState(true);
  const [success, setsuccess] = useState();
  const [ex, setex] = useState([]);
  const [themes, setThemes] = useState([]);
  const [selectedThemeId, setSelectedThemeId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state || {};
  const { t, i18n } = useTranslation();
  const currentlng = localStorage.getItem("i18nextLng") || i18n.language || "en";

  const normalizeDurationForAttach = (value) => {
    const parsed = parseInt(String(value ?? "").trim(), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  };

  const loadThemes = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${ip_back}api/admin/getTheme`, {
        headers: { Authorization: token },
      });
      const rows = response.data?.data || [];
      setThemes(rows);
      const technical = rows.find(
        (th) =>
          String(th.theme_name_en || "")
            .trim()
            .toLowerCase() === DEFAULT_THEME_EN.toLowerCase()
      );
      const fallback = technical || rows[0];
      if (fallback) {
        setSelectedThemeId((prev) => prev || themeIdOf(fallback));
      }
    } catch (err) {
      console.error("Error loading themes:", err);
    }
  }, []);

  useEffect(() => {
    loadThemes();
  }, [loadThemes]);

  useEffect(() => {
    typelist();
  }, [data.sessionid, data.grpid, data.cbid, data.typeid, data.subtypeid]);

  /** Enrich minimal exercise list with full details (type, subtype, image, themes, skills, focus) so cards match personalized list design */
  const enrichWithDetails = async (minimalList) => {
    const result = [];
    for (const exItem of minimalList || []) {
      try {
        const { data: resData } = await exdetails({ ex_id: exItem.ex_id });
        const full = resData?.data;
        if (full) {
          result.push({
            ...full,
            ex_id: exItem.ex_id,
            duration: normalizeDurationForAttach(
              exItem.duration ?? full.te_duration ?? 10
            ),
          });
        } else {
          result.push({
            ...exItem,
            duration: normalizeDurationForAttach(exItem.duration ?? 10),
          });
        }
      } catch {
        result.push({
          ...exItem,
          duration: normalizeDurationForAttach(exItem.duration ?? 10),
        });
      }
    }
    return result;
  };

  const typelist = async () => {
    setloading(true);
    seterror("");
    setlist([]);
    setex([]);

    try {
      const params = {
        type_id: data.typeid,
        sub_type_id: data.subtypeid,
        cb_id: data.cbid,
        group_id: data.grpid,
      };
      const response = await getexercise(params);
      const rawList = (response.data.data || []).map((d) => ({
        ...d,
        duration: normalizeDurationForAttach(d.duration ?? 10),
      }));
      const hasFullCardData =
        rawList[0] &&
        (rawList[0].type_ex || rawList[0].type_details) &&
        (rawList[0].sub_type_ex || rawList[0].sub_type_details);
      const data1 = hasFullCardData ? rawList : await enrichWithDetails(rawList);
      setlist(data1);
      if (data1.length === 0) {
        seterror(
          t("te_not_found") || "No exercises found for this competency block."
        );
      }
    } catch (err) {
      console.error(err);
      seterror(err?.message || "Failed to load exercises");
    } finally {
      setloading(false);
    }
  };

  const sortedList = useMemo(
    () => sortExercisesByThemeLevel(list, selectedThemeId, currentlng),
    [list, selectedThemeId, currentlng]
  );

  const handleex = (i) => {
    seterror("");
    if (ex.some((item) => item.ex_id === i.ex_id)) {
      setex((prev) => prev.filter((item) => item.ex_id !== i.ex_id));
    } else {
      setex((prev) => [
        ...prev,
        {
          ex_id: i.ex_id,
          duration: normalizeDurationForAttach(i.duration),
          steps: "1",
        },
      ]);
    }
  };

  const handleadd = async () => {
    if (ex.length === 0) {
      seterror(`${t("please_select_1ex")}`);
    } else {
      setloading(true);
      try {
        const normalizedSelectedExercises = ex.map((item) => ({
          ...item,
          duration: normalizeDurationForAttach(item.duration),
          ...(data.cbid ? { cb_id: data.cbid } : {}),
        }));
        const params = {
          ex_ids: normalizedSelectedExercises,
          ts_id: data.sessionid,
          allow_duration_exceed: true,
          tsg_id: data.tsg_id || (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("planning_tsg_id") : null),
          te_id: data.te_id || undefined,
        };
        const response = await addexercise(params);
        setloading(false);
        setsuccess(response.data.message);
        setopen(true);
        setex([]);
        setTimeout(() => {
          navigate("/planning");
        }, 1000);
      } catch (error) {
        setloading(false);
        seterror(error.message);
        console.log(error);
      }
    }
  };

  const handleminchange = (e, i) => {
    seterror("");
    const raw = e.target.value;
    const nextDuration =
      String(raw).trim() === "" ? "" : normalizeDurationForAttach(raw);
    const updatedlist = list.map((d) =>
      d.ex_id === i.ex_id ? { ...d, duration: nextDuration } : d
    );
    setlist(updatedlist);
    const updatedex = ex.map((d) =>
      d.ex_id === i.ex_id ? { ...d, duration: nextDuration } : d
    );
    setex(updatedex);
  };

  return (
    <ExerciseAddLayout>
      <AddHeader title={t("addex")} />
      <div className="expage-nav">
        <p className="expage-nav__breadcrumb">
          {data.type}
          {data.subtype ? ` → ${data.subtype}` : ""}
          {data.cb ? ` → ${data.cb}` : ""}
        </p>
        <div className="expage-nav__actions">
          <span className="expage-nav__count">
            {ex.length} {t("selected") || "selected"}
          </span>
          <button className="expage-nav__add-btn" onClick={handleadd}>
            {t("add")}
          </button>
        </div>
      </div>

      {themes.length > 0 ? (
        <div className="expage-theme-sort" role="group" aria-label={t("themes") || "Themes"}>
          <span className="expage-theme-sort__label">
            {t("themes") || "Themes"}
          </span>
          <div className="expage-theme-sort__chips">
            {themes.map((theme) => {
              const id = themeIdOf(theme);
              const active = String(id) === String(selectedThemeId);
              return (
                <button
                  key={id}
                  type="button"
                  className={`expage-theme-sort__chip${
                    active ? " expage-theme-sort__chip--active" : ""
                  }`}
                  aria-pressed={active}
                  onClick={() => setSelectedThemeId(id)}
                >
                  {themeNameOf(theme, currentlng)}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {error && <p className="error_msg">{error}</p>}

      <div className="expage-grid">
        {sortedList?.map((i, index) => (
          <ExerciseCard
            key={i.ex_id || index}
            exercise={i}
            isSelected={ex.some((item) => item.ex_id === i.ex_id)}
            onToggle={handleex}
            onDurationChange={handleminchange}
            lng={currentlng}
            t={t}
            selectMode="multi"
            showDurationInput
            highlightThemeId={selectedThemeId}
          />
        ))}
      </div>
      <div className="exadd-dock" role="region" aria-label={t("addex")}>
        <span className="exadd-dock__count">
          {ex.length} {t("selected") || "selected"}
        </span>
        <button
          type="button"
          className="exadd-dock__btn"
          disabled={ex.length === 0}
          onClick={handleadd}
          aria-label={t("add")}
        >
          {t("add")}
        </button>
      </div>
      {loading && <Loader />}
      <Snackbar
        open={open}
        autoHideDuration={4000}
        onClose={() => setopen(false)}
      >
        <Alert variant="filled" severity="success">
          {success}
        </Alert>
      </Snackbar>
    </ExerciseAddLayout>
  );
};
